import React, { useState, useMemo, Fragment } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Combobox, Transition } from '@headlessui/react';
import { Job, JobStatus, User } from '@core/generated/gql-types';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { isJobInPast } from '@core/util/job.util';
import {
  formatDate,
  getJobEndTimeString12h,
  getJobStartTimeString12h,
} from '@/utils/utils';
import Button from '../Button';
import CancelJobModal from '../modals/CancelJobModal';
import JobCancelInfo from './JobCancelInfo';

interface JobDetailsProps {
  job: Job;
  availableWorkersForJob: User[];
  selectedWorkerId: string;
  onWorkerSelect: (workerId: string) => void;
  onAssignWorker: () => void;
  isAssigning: boolean;
  onEdit: () => void;
  onCancelSuccess?: () => void;
  showWorkerLink?: boolean;
  canAssignWorker?: boolean;
  canEditJob?: boolean;
}

const CANCELLABLE_STATUSES = [JobStatus.Unfilled, JobStatus.Filled];

const JobDetails: React.FC<JobDetailsProps> = ({
  job,
  availableWorkersForJob,
  selectedWorkerId,
  onWorkerSelect,
  onAssignWorker,
  isAssigning,
  onEdit,
  onCancelSuccess,
  showWorkerLink = true,
  canAssignWorker = true,
}: JobDetailsProps) => {
  const { getWorkerById } = useWorkerStore();

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [workerSearchQuery, setWorkerSearchQuery] = useState('');

  const worker = getWorkerById(job?.userId || '');

  const selectedWorker = useMemo(
    () =>
      availableWorkersForJob.find(
        (availableWorker) => availableWorker.id === selectedWorkerId,
      ) ?? null,
    [availableWorkersForJob, selectedWorkerId],
  );

  const filteredAvailableWorkers = useMemo(() => {
    const trimmedQuery = workerSearchQuery.trim().toLowerCase();
    if (!trimmedQuery) return availableWorkersForJob;
    return availableWorkersForJob.filter((availableWorker) => {
      const fullName =
        `${availableWorker.firstName ?? ''} ${availableWorker.lastName ?? ''}`.toLowerCase();
      const email = availableWorker.email?.toLowerCase() ?? '';
      return fullName.includes(trimmedQuery) || email.includes(trimmedQuery);
    });
  }, [availableWorkersForJob, workerSearchQuery]);

  const isFutureJob = dayjs(Number(job?.startDate)).isAfter(dayjs());
  const isCompletedJob = dayjs(Number(job?.endDate)).isBefore(dayjs());
  const showEditButton =
    (job?.itemStatus === JobStatus.Unfilled ||
      job?.itemStatus === JobStatus.Draft ||
      job?.itemStatus === JobStatus.Filled) &&
    (isFutureJob || isCompletedJob);

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <JobCancelInfo job={job} showWorkerLink={showWorkerLink} />
      <div className="mb-6">
        {worker ? (
          <div>
            <p className="text-sm font-medium text-gray-500">Accepted by</p>
            <p className="mt-1 text-sm text-gray-900">
              {showWorkerLink ? (
                <Link
                  to={`/workers/${worker.id}`}
                  className="text-blue-600 hover:text-blue-700"
                >
                  {worker?.firstName || 'Unknown Worker'}{' '}
                  {worker?.lastName || ''}
                </Link>
              ) : (
                worker?.firstName || 'Unknown Worker'
              )}{' '}
              {worker?.lastName || ''}
            </p>
          </div>
        ) : (
          canAssignWorker && (
            <div className="flex items-center gap-2">
              <Combobox
                value={selectedWorker}
                onChange={(nextWorker: User | null) => {
                  onWorkerSelect(nextWorker?.id ?? '');
                }}
                disabled={availableWorkersForJob.length === 0}
                immediate
              >
                <div className="relative w-64">
                  <Combobox.Input
                    className="relative w-full cursor-default rounded-lg border border-gray-300 bg-white py-2 pr-10 pl-3 text-left text-sm placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-hidden disabled:cursor-not-allowed disabled:bg-gray-50"
                    displayValue={(comboboxWorker: User | null) =>
                      comboboxWorker
                        ? `${comboboxWorker.firstName ?? ''} ${comboboxWorker.lastName ?? ''}`.trim()
                        : ''
                    }
                    onChange={(event) =>
                      setWorkerSearchQuery(event.target.value)
                    }
                    placeholder={
                      availableWorkersForJob.length === 0
                        ? 'No workers available'
                        : 'Search to assign worker'
                    }
                  />
                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </Combobox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setWorkerSearchQuery('')}
                  >
                    <Combobox.Options className="ring-opacity-5 absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black focus:outline-hidden sm:text-sm">
                      {filteredAvailableWorkers.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          {workerSearchQuery
                            ? 'No workers found matching your search'
                            : 'No workers available'}
                        </div>
                      ) : (
                        filteredAvailableWorkers.map((availableWorker) => (
                          <Combobox.Option
                            key={availableWorker.id}
                            className={({ active }) =>
                              `relative cursor-default py-2 pr-4 pl-10 select-none ${
                                active
                                  ? 'bg-blue-100 text-blue-900'
                                  : 'text-gray-900'
                              }`
                            }
                            value={availableWorker}
                          >
                            {({ selected }) => (
                              <>
                                <span
                                  className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                                >
                                  {availableWorker.firstName}{' '}
                                  {availableWorker.lastName}
                                </span>
                                {selected ? (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                    <svg
                                      className="h-5 w-5"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                      aria-hidden="true"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </span>
                                ) : null}
                              </>
                            )}
                          </Combobox.Option>
                        ))
                      )}
                    </Combobox.Options>
                  </Transition>
                </div>
              </Combobox>
              <Button
                type="button"
                variant="outline"
                onClick={onAssignWorker}
                disabled={!selectedWorkerId || isAssigning}
                className="!w-auto !justify-center !border-gray-300 !bg-white !py-2 !text-sm !font-medium !text-gray-700 hover:!border-gray-300 hover:!bg-gray-50"
              >
                {isAssigning ? 'Assigning...' : 'Assign'}
              </Button>
            </div>
          )
        )}
      </div>

      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">Job Information</h2>
        {showEditButton && (
          <Button
            type="button"
            variant="text"
            onClick={onEdit}
            disabled={!showEditButton}
            className="!h-auto !min-h-0 !w-auto !justify-center !px-0 !py-0 !text-sm !font-medium !text-blue-600 hover:!text-blue-700"
          >
            Edit
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1 text-sm text-gray-900">
                <span
                  className={`rounded-full px-2 py-1 text-xs font-medium ${
                    job?.itemStatus === JobStatus.Filled
                      ? 'bg-green-100 text-green-800'
                      : job?.itemStatus === JobStatus.Unfilled
                        ? 'bg-yellow-100 text-yellow-800'
                        : job?.itemStatus === JobStatus.Cancelled
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {job.itemStatus || 'Unknown'}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Description</dt>
              <dd className="mt-1 text-sm whitespace-pre-line text-gray-900">
                {job.description || 'No description provided'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Start date</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {`${getJobStartTimeString12h(job)}, ${formatDate(job.startDate || '')}`}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">End date</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {`${getJobEndTimeString12h(job)}, ${formatDate(job.endDate || '')}`}
              </dd>
            </div>
          </dl>
        </div>

        <div>
          {job.addressHash ? (
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Address</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {job.address?.addressFriendly}
                </dd>
              </div>
            </dl>
          ) : (
            <p className="text-sm text-gray-500">
              No location information available
            </p>
          )}
        </div>
      </div>

      {CANCELLABLE_STATUSES.includes(job?.itemStatus as JobStatus) &&
        !isJobInPast(job) && (
          <div className="mt-6 border-t border-gray-200 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsCancelModalOpen(true)}
              className="!rounded-lg !border-red-600 !bg-red-50 !py-2 !text-sm !text-red-700 hover:!border-red-600 hover:!bg-red-100"
            >
              Cancel job
            </Button>
          </div>
        )}

      <CancelJobModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        job={job}
        onSuccess={() => {
          onCancelSuccess?.();
          toast.success('Job cancelled successfully', {
            position: 'bottom-left',
            autoClose: 3000,
          });
        }}
      />
    </div>
  );
};

export default JobDetails;
